/**
 * Points an issue costs the product's OVERALL score. The service scores each field from 100 down
 * by its failed checks' deductions and averages fields by weight over the fields it scored, so
 * one failure moves the overall by deduction × weight ÷ (sum of the scored fields' weights).
 */
import type { CheckResult } from '../types/quality-scores.types';

import { fieldWeight, scoredWeightTotal } from './field-weights';
import { severityDeduction } from './quality-impact';

/** A field's full score — what an empty asked field loses. */
const FULL_FIELD = 100;

/** Whether a check is the field's presence check (`title.present`, `gtin.present` …). */
export function isPresenceCheck(checkId: string): boolean {
  return checkId.endsWith('.present');
}

/**
 * Overall points one check costs, rounded to one decimal. A passed check, a field that wasn't
 * scored, or an unweighted field costs 0; a failed presence check on a scored (asked) field
 * costs the whole field (100 × weight ÷ sum); any other failure costs its severity deduction.
 */
export function pointsLost(
  check: Pick<CheckResult, 'checkId' | 'passed' | 'severity'>,
  field: string,
  scoredFields: readonly string[],
): number {
  if (check.passed || !scoredFields.includes(field)) return 0;
  const total = scoredWeightTotal(scoredFields);
  if (total <= 0) return 0;
  const deduction = isPresenceCheck(check.checkId) ? FULL_FIELD : severityDeduction(check.severity);
  return Math.round(((deduction * fieldWeight(field)) / total) * 10) / 10;
}
